import {
  Activity,
  ArrowRight,
  Building2,
  Check,
  Clock3,
  Flame,
  MapPinned,
  Radio,
  ShieldCheck,
  Siren,
  Truck,
} from 'lucide-react';
import { Brand } from './App';

export default function Landing({ onStart }: { onStart: () => void }) {
  return (
    <div className="landing-page">
      <header className="landing-header">
        <Brand />
        <nav aria-label="Startseitennavigation">
          <a href="#funktionen">Funktionen</a>
          <a href="#realismus">Realismus</a>
          <span className="landing-online">
            <i /> System bereit
          </span>
          <button className="landing-header-start" onClick={onStart}>
            Anmelden / Registrieren <ArrowRight size={16} />
          </button>
        </nav>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="landing-kicker">
              <span>112BASE</span> DEUTSCHE LEITSTELLEN-SIMULATION
            </p>
            <h1>
              Deine Leitstelle.
              <br />
              <em>Dein Einsatzgebiet.</em>
            </h1>
            <p className="landing-lead">
              Übernimm eine integrierte Leitstelle, disponiere Feuerwehr, Rettungsdienst und Polizei
              und führe dynamische Einsatzlagen vom Notruf bis zum Abschluss.
            </p>
            <div className="landing-actions">
              <button className="landing-primary" onClick={onStart}>
                <Siren size={19} /> Kostenlos registrieren <ArrowRight size={17} />
              </button>
              <a className="landing-secondary" href="#funktionen">
                Funktionsumfang ansehen
              </a>
            </div>
            <div className="landing-assurances" aria-label="Produkteigenschaften">
              <span>
                <Check size={14} /> Im Browser
              </span>
              <span>
                <Check size={14} /> Persistenter Spielstand
              </span>
              <span>
                <Check size={14} /> Deutschlandweit
              </span>
            </div>
          </div>

          <div className="landing-preview" aria-label="Vorschau des Leitstellen-Arbeitsplatzes">
            <div className="preview-glow" />
            <div className="preview-window">
              <div className="preview-topline">
                <div>
                  <span className="preview-caption">LIVE-LAGE</span>
                  <strong>Leitstelle Stuttgart</strong>
                </div>
                <span className="preview-clock">
                  <i /> 14:27:36
                </span>
              </div>
              <div className="preview-map">
                <span className="map-road road-one" />
                <span className="map-road road-two" />
                <span className="map-road road-three" />
                <span className="map-district district-one">Feuerbach</span>
                <span className="map-district district-two">Bad Cannstatt</span>
                <span className="map-district district-three">Stuttgart</span>
                <span className="preview-route route-fire" />
                <span className="preview-route route-medical" />
                <span className="preview-unit unit-fire">
                  <Truck size={13} /> HLF 1/23-1
                </span>
                <span className="preview-unit unit-medical">
                  <Activity size={13} /> RTW 3/83-1
                </span>
                <div className="preview-incident">
                  <span>
                    <Flame size={18} />
                  </span>
                  <div>
                    <small>B3 · BRAND</small>
                    <strong>Gebäudebrand</strong>
                  </div>
                </div>
              </div>
              <div className="preview-status">
                <span>
                  <b>4</b> Aktive Einsätze
                </span>
                <span>
                  <b>12 / 18</b> Kräfte verfügbar
                </span>
                <span>
                  <b>7</b> Auf Anfahrt
                </span>
              </div>
            </div>
            <aside className="preview-dispatch-card">
              <span className="dispatch-code">B3</span>
              <div>
                <small>NEUER EINSATZ</small>
                <strong>Gebäudebrand</strong>
                <p>Rauchentwicklung aus dem 2. OG</p>
              </div>
              <span className="dispatch-time">14:26</span>
            </aside>
          </div>
        </section>

        <section className="landing-metrics" aria-label="Umfang des Simulators">
          <div>
            <strong>226</strong>
            <span>Leitstellenbereiche</span>
          </div>
          <div>
            <strong>38.742</strong>
            <span>kartierte BOS-Standorte</span>
          </div>
          <div>
            <strong>26</strong>
            <span>Fahrzeug- und Einheitentypen</span>
          </div>
          <div>
            <strong>7</strong>
            <span>dynamische Einsatzmodelle</span>
          </div>
        </section>

        <section className="landing-features" id="funktionen">
          <div className="landing-section-heading">
            <p className="landing-kicker">EINSATZFÜHRUNG IM DETAIL</p>
            <h2>Ein Arbeitsplatz. Die gesamte Lage.</h2>
            <p>
              Klar aufgebaut wie eine professionelle Leitstelle und mit den Werkzeugen, die für
              glaubwürdige Entscheidungen zählen.
            </p>
          </div>
          <div className="landing-feature-grid">
            <article>
              <span className="feature-icon blue">
                <MapPinned size={22} />
              </span>
              <small>01 · EINSATZGEBIET</small>
              <h3>Reale Zuständigkeiten</h3>
              <p>
                Wähle deine Leitstelle. Reale Kreisgrenzen bestimmen Einsatzgenerator, Standortsuche
                und überörtliche Kräfte.
              </p>
            </article>
            <article>
              <span className="feature-icon red">
                <Radio size={22} />
              </span>
              <small>02 · DISPOSITION</small>
              <h3>Kräfte intelligent führen</h3>
              <p>
                Alarmiere verfügbare Einheiten, verfolge Status, Anfahrt und ETA und reagiere auf
                neue Lagemeldungen.
              </p>
            </article>
            <article>
              <span className="feature-icon amber">
                <Clock3 size={22} />
              </span>
              <small>03 · DYNAMIK</small>
              <h3>Lagen entwickeln sich</h3>
              <p>
                Einsätze eskalieren zeitabhängig. Maßnahmen, Kräfteansatz und Reaktionszeit
                beeinflussen den Verlauf.
              </p>
            </article>
          </div>
        </section>

        <section className="landing-realism" id="realismus">
          <div>
            <p className="landing-kicker">REALISTISCH. NACHVOLLZIEHBAR. ERWEITERBAR.</p>
            <h2>Für komplexe Lagen gebaut.</h2>
            <p>
              Feuerwehr, Rettungsdienst, Hilfsorganisationen, HvO, Polizei, THW, Wasser- und
              Bergrettung greifen in einer gemeinsamen Simulation ineinander.
            </p>
            <ul>
              <li>
                <ShieldCheck size={17} /> Professionelle Leitstellenoberfläche ohne Game-Look
              </li>
              <li>
                <Building2 size={17} /> Wachen und Ressourcen frei verwaltbar
              </li>
              <li>
                <Activity size={17} /> Timeline, Maßnahmen und KI-Lagebilder
              </li>
            </ul>
          </div>
          <div className="landing-cta-card">
            <span className="cta-pulse">
              <Siren size={28} />
            </span>
            <small>BEREIT ZUR DIENSTÜBERNAHME</small>
            <h2>Die nächste Lage wartet nicht.</h2>
            <p>Öffne deinen Arbeitsplatz und übernimm die Einsatzführung.</p>
            <button className="landing-primary" onClick={onStart}>
              Leitstelle öffnen <ArrowRight size={17} />
            </button>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <Brand />
        <p>Realitätsnahe Einsatzsimulation für Ausbildung, Planung und Simulation.</p>
        <span>112BASE · LEITWERK</span>
      </footer>
    </div>
  );
}
